import { z } from 'zod';

/**
 * Which behavioural heuristic raised a flag — `02-build.md` §P27's three, named.
 *
 * - `rapid_match_cycling` — matching and un-matching in quick succession.
 * - `mass_like_velocity` — liking far more accounts in a window than a member
 *   reading profiles could.
 * - `message_spam` — sending far more messages in a window than a conversation
 *   would produce.
 *
 * **All three are declared here before any of them is computed** (P27a2 does
 * that), and the reason is the Postgres enum this backs: a value added to a
 * *pre-existing* type cannot be named by another statement in the same
 * migration batch, because drizzle wraps the whole batch in one transaction
 * (P24b2) — while a type created in that batch may be named freely (P25c). So
 * the closed list lands with the type, and P27a2 writes rows instead of a
 * migration it would have to split and could not.
 *
 * Each rule maps to a seeded `violation_types.key` of the same name, which is
 * what gives a behavioural flag the configured action, severity and
 * review-before-enforce a message violation already gets.
 */
export const VIOLATION_BEHAVIOR_RULES = [
  'rapid_match_cycling',
  'mass_like_velocity',
  'message_spam',
] as const;

export const ViolationBehaviorRuleSchema = z.enum(VIOLATION_BEHAVIOR_RULES);

export type ViolationBehaviorRule = (typeof VIOLATION_BEHAVIOR_RULES)[number];

/**
 * Is this violation-type key one of the behavioural rules?
 *
 * The key IS the rule, so the question is answered by the list above — and it
 * is asked in three places that must agree: the api keeps these keys out of the
 * classifier's label set, refuses to switch `reviewBeforeEnforce` off for them
 * (the detector always queues, so "off" would be a setting nothing reads), and
 * the admin locks the same switch and badges the row. One predicate, or the
 * three drift.
 */
export function isBehaviorViolationTypeKey(key: string): key is ViolationBehaviorRule {
  return ViolationBehaviorRuleSchema.safeParse(key).success;
}
